// src/runtime/SchedulerRuntime/types.ts
// ============================================
// SchedulerRuntime - 类型定义（冻结版本 v1.0）
//
// 职责：定时任务调度（Cron / Interval / OneShot）
//
// 冻结声明：此文件为 v1.0 冻结版本，新增类型只追加不修改
// ============================================

// ============================================
// 任务类型
// ============================================

export enum ScheduleType {
  /** Cron 表达式 */
  Cron = 'cron',
  /** 固定间隔 */
  Interval = 'interval',
  /** 一次性定时 */
  OneShot = 'one_shot',
  /** P2-1 新增：Workflow 任务（无人值守自动执行） */
  Workflow = 'workflow',
}

// ============================================
// 调度任务
// ============================================

export interface ScheduledTask {
  /** 任务 ID */
  id: string;
  /** 任务名称 */
  name: string;
  /** 任务类型 */
  type: ScheduleType;
  /** Cron 表达式（type=cron 时使用） */
  cron?: string;
  /** 间隔毫秒（type=interval 时使用） */
  interval?: number;
  /** 执行时间（type=one_shot 时使用，ISO 字符串） */
  scheduledAt?: string;
  /** 任务回调（调用 Runtime + Command） */
  action: TaskAction;
  /** 是否启用 */
  enabled: boolean;
  /** 上次执行时间 */
  lastRunAt?: string;
  /** 下次执行时间 */
  nextRunAt?: string;
  /** 执行次数 */
  runCount: number;
  /** 最大执行次数（0=无限） */
  maxRuns?: number;
  /** 创建时间 */
  createdAt: string;
}

// ============================================
// 任务动作
// ============================================

export interface TaskAction {
  /** 目标 Runtime */
  runtime: string;
  /** 执行命令 */
  command: string;
  /** 参数 */
  params?: Record<string, unknown>;
}

// ============================================
// 执行记录
// ============================================

export interface TaskExecutionRecord {
  /** 任务 ID */
  taskId: string;
  /** 任务名称 */
  taskName: string;
  /** 执行开始时间 */
  startedAt: string;
  /** 执行结束时间 */
  finishedAt: string;
  /** 是否成功 */
  success: boolean;
  /** 结果 */
  result?: unknown;
  /** 错误信息 */
  error?: string;
  /** 耗时（毫秒） */
  duration: number;
}

// ============================================
// 查询/命令枚举
// ============================================

export enum SchedulerQuery {
  /** 列出所有任务 */
  List = 'list',
  /** 获取任务详情 */
  Get = 'get',
  /** 获取下次执行时间 */
  GetNextRun = 'get_next_run',
  /** 获取执行历史 */
  GetHistory = 'get_history',
  /** P2-1 新增：列出所有 AutoAgent 自动任务 */
  AutoAgentList = 'auto_agent_list',
}

export enum SchedulerCommand {
  /** 创建任务 */
  Create = 'create',
  /** 更新任务 */
  Update = 'update',
  /** 删除任务 */
  Delete = 'delete',
  /** 暂停任务 */
  Pause = 'pause',
  /** 恢复任务 */
  Resume = 'resume',
  /** 立即触发 */
  Trigger = 'trigger',
  /** P2-1 新增：创建 AutoAgent 自动任务 */
  CreateAutoAgent = 'create_auto_agent',
  /** P2-1 新增：删除 AutoAgent 自动任务 */
  DeleteAutoAgent = 'delete_auto_agent',
  /** P2-1 新增：立即触发 AutoAgent 自动任务 */
  TriggerAutoAgent = 'trigger_auto_agent',
}

// ============================================
// 统一返回类型
// ============================================

export interface SchedulerResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}

// ============================================
// P2-1 新增：AutoAgent 自动任务（无人值守）
// ============================================

/**
 * AutoAgent 自动任务
 *
 * 用于无人值守场景：Scheduler 触发 → Workflow 执行 → Notification 通知 → Memory 记录
 */
export interface AutoAgentTask {
  /** 任务 ID */
  id: string;
  /** 任务名称 */
  name: string;
  /** Cron 表达式（5 字段：分 时 日 月 周） */
  cron: string;
  /** 关联的 Workflow ID（执行时调用 WorkflowRuntime） */
  workflowId: string;
  /** 是否启用 */
  enabled: boolean;
  /** 上次执行时间 */
  lastRunAt?: string;
  /** 下次执行时间 */
  nextRunAt?: string;
  /** 已执行次数 */
  runCount: number;
  /** 最大重试次数（默认 3） */
  maxRetries: number;
  /** 当前已重试次数 */
  retryCount: number;
  /** 创建时间 */
  createdAt: string;
}
